import { Collapse, Grid } from '@arco-design/web-react';
import { useFocusIdx } from '@bay-email/editor';
import React from 'react';
import { ContainerBackgroundColor } from '../../attributes/ContainerBackgroundColor';
import { Padding } from '../../attributes/Padding';
import { Width } from '../../attributes/Width';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { TextField, ColorPickerField, NumberField } from '@extensions/components/Form';
import { pixelAdapter } from '../../adapter';
import { t } from '@bay-email/core';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';
import { ResetInheritedTypography } from '../shared/ResetInheritedTypography';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import { usesListingMergeTags } from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

export function Table() {
	const { focusIdx } = useFocusIdx();
	const { selectedNode } = useSelectedNode();
	const tableContent = String(selectedNode?.data?.value?.content || '');
	const showListingSettings = React.useMemo(
		() => usesListingMergeTags(tableContent),
		[tableContent],
	);
	useClearAttachedListingWhenUnused(selectedNode, showListingSettings);
	const defaultActiveKey = React.useMemo(
		() => (showListingSettings ? ['-1', '0', '1'] : ['0', '1']),
		[showListingSettings],
	);

	return (
		<CollapseWrapper
			defaultActiveKey={defaultActiveKey}
			style={{ padding: 0 }}
		>
			{showListingSettings && (
				<Collapse.Item
					name='-1'
					header={t('Listing Settings')}
					contentStyle={{ padding: 0 }}
				>
					<AttachedListingSection observedValues={[tableContent]} />
				</Collapse.Item>
			)}
			<Collapse.Item
				name='0'
				header={t('Content Block Settings')}
			>
				<div className='px-2 py-3'>
					{/* Background color - Full Width */}
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '16px' }}
					>
						<Grid.Col span={24}>
							<ContainerBackgroundColor title={t('Background color')} />
						</Grid.Col>
					</Grid.Row>
					<div style={{ marginBottom: '16px' }}>
						<ResetInheritedTypography />
					</div>
					{/* Table border and Cell border color in two-column layout */}
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '16px' }}
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<TextField
									label={t('Table Border')}
									name={`${focusIdx}.attributes.border`}
									placeholder='e.g. 1px solid #ddd'
								/>
							</div>
						</Grid.Col>
						<Grid.Col span={12}>
							<div style={{ paddingLeft: '8px' }}>
								<ColorPickerField
									label={t('Cell border color')}
									name={`${focusIdx}.attributes.cellBorderColor`}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					{/* Cell padding in half-width layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<NumberField
									label={t('Cell padding (px)')}
									name={`${focusIdx}.attributes.cellPadding`}
									config={pixelAdapter}
									max={20}
									min={0}
									step={1}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>

			<Collapse.Item
				name='1'
				header={t('Dimension')}
			>
				<div className='px-2 py-3'>
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '16px' }}
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<Width
									autoEnabled
									config={pixelAdapter}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<Padding />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
